import { Sheet } from './Sheet';
import { Tile, type TileVisualState } from './Tile';
import { useStrings } from '../state/AppContext';

export interface HowToPlaySheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Tile states in the order the example letters explain them. Narrower
 * than `TileVisualState` on purpose: only the three *scored* states have
 * an example line, and typing it this way is what makes the copy lookup
 * below total.
 */
const EXAMPLE_STATES = ['correct', 'present', 'absent'] as const;

export function HowToPlaySheet({ open, onClose }: HowToPlaySheetProps) {
  const t = useStrings();
  // The letters come from the copy table too: they spell the start of the
  // language's own word for "word", and a language could need different
  // ones (spec §10.2 keeps the board to plain a-z, so they always can).
  const examples = EXAMPLE_STATES.map((state, i): { state: TileVisualState; letter: string; text: string } => ({
    state,
    letter: t.howTo.exampleLetters[i] ?? '',
    text: t.howTo.example[state],
  }));

  return (
    <Sheet open={open} title={t.howTo.title} onClose={onClose}>
      <div className="flex flex-col gap-3 pb-4 text-sm">
        <p>{t.howTo.intro}</p>
        <p className="text-dim">{t.howTo.realWord}</p>

        {examples.map(({ letter, state, text }) => (
          <div key={state} className="flex items-center gap-3">
            <div className="h-11 w-11 shrink-0">
              <Tile letter={letter} state={state} row={1} col={1} />
            </div>
            <p className="text-dim">{text}</p>
          </div>
        ))}

        <p>{t.howTo.ladder}</p>
        <p className="text-dim">{t.howTo.journey}</p>
        <p className="text-dim">{t.howTo.hints}</p>
        <p className="text-dim">{t.howTo.languages}</p>
      </div>
    </Sheet>
  );
}
